import {
  isStripeBillingStrategy,
  type PaymentPlan,
  type PaymentPlanDiscount,
  type PriceVO,
  type Suspend,
} from '@ctct/domain-layer';
import { envs, urlParams } from '@ctct/infrastructure-layer';
import type { PrepaySelectPageStore } from '@ctct/state-client-layer';

import type { TEventSource } from '../../../helpers';
import { prepaySelectEventBus } from '../PrepaySelectEventBus';
import type { Flags, PrepayOptionsStrategy, PrepaySelectUrlAdapter } from '../types';

function shouldSetPrepayTo1(flags: Flags, paymentPlan: PaymentPlan | null): boolean {
  if (!isStripeBillingStrategy(flags.billingStrategy) || flags.disallowSelectionOfShorterPaymentPlan) {
    return false;
  }

  if (!paymentPlan) {
    return true;
  }

  const currentPeriod = paymentPlan.getCurrentPaymentPeriod();
  const isStripe = paymentPlan.getIsStripe();

  return !isStripe || currentPeriod <= 1;
}

export class PrepayService {
  constructor(
    private pageStore: PrepaySelectPageStore,
    private paymentPlan: PaymentPlan,
    private paymentPlanDiscountOptionsStrategy: PrepayOptionsStrategy,
    private urlAdapter: PrepaySelectUrlAdapter,
    private flags: Flags,
    private suspend?: Suspend | null
  ) {
    if (this.suspend) {
      this.handlePrepayChange('0', 'onMount');
    } else if (flags.isSocialStarter) {
      this.handlePrepayChange('1', 'onMount');
    } else if (
      shouldSetPrepayTo1(flags, this.paymentPlan) &&
      pageStore.getState().prepay !== '1' &&
      !this.hasPaymentPeriodInUrlOrStorage()
    ) {
      this.handlePrepayChange('1', 'onMount');
    }
  }

  public api() {
    return {
      setPrepay: (prepay: string) => this.handlePrepayChange(prepay, 'api'),
      setPlanBaseMonthlyPrice: (planBaseMonthlyPrice: { value: PriceVO; currency: string } | null) => {
        this.pageStore.setState({ planBaseMonthlyPrice });
      },
      getPrepay: () => this.getPrepayPeriod(),
      disable: () => this.pageStore.setState({ prepaySelectDisabled: true }),
      enable: () => this.pageStore.setState({ prepaySelectDisabled: false }),
    };
  }

  public data() {
    return {
      getPeriodOptions: () => {
        const periodOptions = this.filterRemove6Month(
          this.paymentPlanDiscountOptionsStrategy.getAvailablePeriodOptions(this.paymentPlan)
        );
        const displayParameters = this.paymentPlanDiscountOptionsStrategy.getCompactPeriodDisplayParameters(
          periodOptions,
          this.paymentPlan
        );

        return this.disableOptionsForCoreMutationLockedAccount(displayParameters);
      },

      getCustomerPeriodOptions: (planBaseMonthlyPrice: null | { value: PriceVO; currency: string }) => {
        const periodOptions = this.filterRemove6Month(
          this.paymentPlanDiscountOptionsStrategy.getAvailablePeriodOptions(this.paymentPlan)
        );
        const displayParameters = this.paymentPlanDiscountOptionsStrategy.getVerbosePeriodDisplayParameters(
          periodOptions,
          this.paymentPlan,
          planBaseMonthlyPrice
        );

        return this.disableOptionsForCoreMutationLockedAccount(displayParameters);
      },
    };
  }

  public handlers() {
    return {
      onPrepayChange: (prepay: string) => this.handlePrepayChange(prepay, 'user'),
    };
  }

  public onMount() {
    if (this.flags.isSingleBillChildAccount) {
      if (this.flags.isCoreMutationLockPending) return;

      const currentPrepayPeriod = String(this.paymentPlan.getCurrentPaymentPeriod());

      this.handlePrepayChange(currentPrepayPeriod, 'onMount');

      if (envs.isSecure()) urlParams.deletePaymentPeriod();

      return;
    }

    const prepayPeriodToMount = this.getPrepayPeriodToMount();

    this.handlePrepayChange(prepayPeriodToMount, 'onMount');

    if (envs.isSecure()) urlParams.deletePaymentPeriod();
  }

  /**
   * Determine which prepay period to mount.
   *
   * If an invalid prepay period is provided via URL, default to the first valid prepay period.
   */
  private getPrepayPeriodToMount() {
    const { skipInitializationFromUrl, isCustomer, isSocialStarter } = this.flags;

    if (isSocialStarter) {
      return '1';
    }

    const prepayFromUrl = this.getPaymentPeriodFromUrlOrStorage();

    if (prepayFromUrl && !skipInitializationFromUrl) {
      const isValid = this.paymentPlanDiscountOptionsStrategy.getIsPrepayPeriodValid(this.paymentPlan, prepayFromUrl);
      const isRemovedByExperiment = this.flags.isRemove6MoPrepayEnabled && prepayFromUrl === '6';

      if (isValid && !isRemovedByExperiment) {
        return prepayFromUrl;
      }

      return this.paymentPlanDiscountOptionsStrategy.getPreselectedPeriod(this.paymentPlan);
    }

    if (this.flags.hasCharmPricing) {
      // fallback to monthly if charm pricing is enabled and no prepay period is provided via URL
      return '1';
    }

    if (isCustomer && !this.flags.isStripePhase2Enabled) {
      return this.paymentPlanDiscountOptionsStrategy.getPreselectedPeriod(this.paymentPlan);
    }

    if (this.flags.isValueFocusedMWebPlanSelectionEnabled && !isCustomer) {
      return '1';
    }

    return this.getPrepayPeriod();
  }

  private getPrepayPeriod() {
    return this.pageStore.getState().prepay;
  }

  private getPaymentPeriodFromUrlOrStorage(): string | null {
    return this.urlAdapter.getPrepaySelectParams()?.paymentPeriod ?? null;
  }

  private hasPaymentPeriodInUrlOrStorage(): boolean {
    return !!this.getPaymentPeriodFromUrlOrStorage();
  }

  private handlePrepayChange(prepay: string, source: TEventSource) {
    if (this.flags.isSingleBillChildAccount && (source === 'user' || source === 'api')) {
      return;
    }

    const effectivePrepay = this.flags.isSocialStarter ? '1' : prepay;

    this.pageStore.setState({ prepay: effectivePrepay });
    prepaySelectEventBus.emit('prepayChanged', effectivePrepay, { source });
  }

  private disableOptionsForCoreMutationLockedAccount<T extends { disabled?: boolean }>(options: T[]): T[] {
    if (!this.flags.isSingleBillChildAccount) return options;

    return options.map((option) => ({
      ...option,
      disabled: true,
    }));
  }

  private filterRemove6Month(options: PaymentPlanDiscount[]): PaymentPlanDiscount[] {
    if (!this.flags.isRemove6MoPrepayEnabled) return options;

    return options.filter((opt) => opt.discountPeriod !== 6);
  }
}
